import {
    type FC,
    type MouseEvent,
    type ReactNode,
    useContext,
} from "react";
import { Link } from "react-router-dom";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { useShopfrontDispatch } from "../../_Bootstrap/hooks";
import { navigationClicked } from "../../Actions/General/NavigationActions";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { NavigationGroupChildFocus } from "./NavigationGroup";

export type ToCallback = (e: MouseEvent<HTMLButtonElement>) => void;

interface Props {
    icon?: IconProp;
    to: string | ToCallback;
    children: ReactNode;
    external?: boolean;
    onClickBeforeNavigate?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export const NavigationItem: FC<Props> = ({
    icon: iconProp,
    to,
    children,
    external,
    onClickBeforeNavigate,
}) => {
    const dispatch = useShopfrontDispatch();

    const onFocus = useContext(NavigationGroupChildFocus);

    let icon: ReactNode = null;

    if(iconProp) {
        icon = (
            <span className="inline-block mr-4 text-primary-dark dark:text-primary">
                <FontAwesomeLight icon={iconProp} />
            </span>
        );
    }

    const onClick = (e: MouseEvent<HTMLAnchorElement>): void => {
        if(typeof onClickBeforeNavigate === "function") {
            onClickBeforeNavigate(e);
        }

        if(typeof to === "function") {
            return;
        }

        if(external) {
            window.location.href = to;

            return;
        }

        dispatch(navigationClicked());
    };

    let content: ReactNode;

    if(typeof to === "function") {
        content = (
            <button
                onClick={to}
                onFocus={onFocus}
                className={
                    "border-0 bg-transparent text-xl py-1 px-0 m-0 inline-block w-full text-left " +
                    "ignore-default text-neutral-900 hover:text-neutral-500 dark:text-neutral-100 " +
                    "dark:hover:text-neutral-400 cursor-pointer"
                }
            >
                {icon}
                {children}
            </button>
        );
    } else if(external) {
        content = (
            <a
                href={to}
                onClick={onClick}
                onFocus={onFocus}
                className={
                    "text-neutral-900 hover:text-neutral-500 dark:text-neutral-100 dark:hover:text-neutral-400" +
                    " no-underline inline-block w-full ignore-default py-1 cursor-pointer"
                }
            >
                {icon}
                {children}
            </a>
        );
    } else {
        content = (
            <Link
                to={to}
                onClick={onClick}
                onFocus={onFocus}
                className={
                    "text-neutral-900 hover:text-neutral-500 dark:text-neutral-100 dark:hover:text-neutral-400 " +
                    "no-underline inline-block w-full ignore-default py-1 cursor-pointer"
                }
            >
                {icon}
                {children}
            </Link>
        );
    }

    return (
        <li className="text-xl my-1 truncate w-full">
            {content}
        </li>
    );
};
